import SectionContainer from '@common/components/SectionContainer';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { JourneyPickerV2Connected } from '@features/JourneyPickerV2/container';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useTranslations } from '@lang';
import { FC, useCallback, useMemo } from 'react';
import { useRouter } from 'shared-routing';
import { Category } from 'shared-types/Common';
import { Property } from 'shared-types/Property';
import { SpaListTemplate } from 'voyager';

import { buildCategoryItems } from './utils';

type CategoryV2Props = {
  property: Property;
  category: Category;
};

export const CategoryV2: FC<CategoryV2Props> = ({ property, category }) => {
  const router = useRouter();
  const { t } = useTranslations();
  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  const labels = useMemo(
    () => ({
      spaDay: t('spa.spaDay'),
      treatment: t('spa.spaTreatment'),
      facility: t('spa.facility'),
      product: t('product.label'),
    }),
    [t]
  );

  const { items, routesById } = useMemo(
    () =>
      buildCategoryItems({
        category,
        labels,
        fallbackImageUrl: property?.socialFallbackImage?.url,
      }),
    [category, labels, property?.socialFallbackImage?.url]
  );

  const handleSelectItem = useCallback(
    (compositeId: string) => {
      const target = routesById[compositeId];
      if (!target) return;
      router.push({ pathname: target.route, query: { id: target.id } });
    },
    [router, routesById]
  );

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper>
        <JourneyPickerInnerWrapper>
          <JourneyPickerV2Connected
            dateMode="single"
            dateContext="spa"
            showGuests
            numberOfMonths={1}
            categories={navCategories}
            category="collections"
            onCategoryChange={handleNavCategoryChange}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SectionContainer>
        <SpaListTemplate
          items={items}
          onSelectItem={handleSelectItem}
          titleLabel={category.name}
          fromPriceLabel={t('common.from')}
          soldOutLabel={t('spa.soldOut')}
          noResultsLabel={t('category.empty')}
          noImageLabel={t('spa.noImage')}
          columns={3}
          imageAspectRatio="3 / 2"
        />
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
